import { useMutation, useQueryClient } from '@tanstack/react-query';
import { fileMail } from '@/services/api/endpoints';
import { AfterSendError, sendThenCapture } from '@/services/graph/sendAndFile';
import { addBreadcrumb } from '@/services/breadcrumbs/breadcrumbs';
import { reportError } from '@/services/errors/errorReporter';
import type { DuplicateExisting } from '@/features/file-mail/useFileMail';

export interface SendAndFileArgs {
  /** Dossier de destination, ou `null` pour la racine de l'espace (cf. useFileMail). */
  targetFolderId: string | null;
  /** Espace de destination. Requis même avec un dossier : il porte la racine. */
  targetContainerId: string;
}

/**
 * Issue d'un « Envoyer et ranger ».
 *
 * TROIS ISSUES ET NON DEUX, parce que « envoyé mais pas rangé » n'est ni un succès
 * ni un échec ordinaire : le mail est chez le destinataire, il n'y a rien à
 * reprendre, seulement un rangement à refaire à la main. La confondre avec un
 * échec pousserait à renvoyer le mail ; la confondre avec un succès laisserait
 * croire l'archive à jour. Un échec AVANT l'envoi, lui, remonte en exception :
 * rien n'est parti, le brouillon est intact, réessayer est sans danger.
 */
export type SendAndFileResult =
  | { status: 'filed'; subject: string; mailId: string }
  | { status: 'duplicate'; subject: string; existing?: DuplicateExisting }
  | { status: 'sent-not-filed'; subject: string; message: string };

/**
 * Envoie le brouillon, puis range la copie envoyée. Rend l'issue.
 *
 * EXTRAITE DU HOOK À DESSEIN : tout ce qui compte ici est un enchaînement d'appels
 * et un aiguillage à trois issues, sans rien de React. La sortir permet de
 * l'éprouver directement — sans monter de composant ni de `QueryClient` — et c'est
 * ainsi qu'on a pu VÉRIFIER, et non espérer, qu'un échec postérieur à l'envoi ne
 * ressort jamais en exception.
 */
export async function performSendAndFile({
  targetFolderId,
  targetContainerId,
}: SendAndFileArgs): Promise<SendAndFileResult> {
  addBreadcrumb({
    category: 'mutation',
    message: 'send-and-file start',
    data: { folderId: targetFolderId, containerId: targetContainerId },
  });

  let handle;
  try {
    handle = await sendThenCapture();
  } catch (err) {
    if (err instanceof AfterSendError) {
      // Parti, mais introuvable ou illisible. On le DIT, on ne lève pas.
      reportError({ error: err, tags: ['send-and-file', 'after-send'] });
      return { status: 'sent-not-filed', subject: '', message: err.message };
    }
    // Rien n'est parti : vraie erreur, et réessayer doit rester licite.
    throw err instanceof Error ? err : new Error(String(err));
  }

  const { sent, mimeBase64 } = handle;

  try {
    const resp = await fileMail({
      format: 'eml',
      contentBase64: mimeBase64,
      // L'un ou l'autre, jamais les deux — cf. useFileMail.
      ...(targetFolderId ? { targetFolderId } : { targetContainerId }),
      // La copie ENVOYÉE fait foi : ses en-têtes de transport sont définitifs,
      // contrairement à ceux du brouillon (ni `Date:` ni `From:` final).
      metadata: {
        subject: sent.subject,
        internetMessageId: sent.internetMessageId ?? undefined,
        conversationId: sent.conversationId ?? undefined,
        sentAt: sent.sentDateTime ?? undefined,
        fromName: sent.fromName ?? undefined,
        fromAddress: sent.fromAddress ?? undefined,
      },
    });

    if (resp.status === 'created') {
      return { status: 'filed', subject: sent.subject, mailId: resp.mailId };
    }
    return { status: 'duplicate', subject: sent.subject, existing: resp.existing };
  } catch (err) {
    // Le rangement a échoué APRÈS l'envoi : même raisonnement que ci-dessus.
    reportError({
      error: err instanceof Error ? err : new Error(String(err)),
      tags: ['send-and-file', 'file'],
    });
    return {
      status: 'sent-not-filed',
      subject: sent.subject,
      message: err instanceof Error ? err.message : String(err),
    };
  }
}

export function useSendAndFile() {
  const qc = useQueryClient();

  return useMutation<SendAndFileResult, Error, SendAndFileArgs>({
    mutationFn: performSendAndFile,
    onSuccess: (result) => {
      // Rien à rafraîchir quand rien n'a été rangé.
      if (result.status === 'sent-not-filed') return;
      qc.invalidateQueries({ queryKey: ['folders'] });
      // Préfixe `['mails']` : couvre la liste d'un dossier comme celle d'une
      // racine de projet, qui sont deux clés distinctes.
      qc.invalidateQueries({ queryKey: ['mails'] });
    },
  });
}
